import React from 'react';
import { Table, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { formatCurrency, formatPercentage } from '../../utils/currencyUtils';

interface TableItem {
  name: string;
  amount: number;
  percentage: number;
  link: string;
}

interface DataTableProps {
  title: string;
  data: TableItem[];
  maxHeight?: string;
}

const DataTable: React.FC<DataTableProps> = ({ 
  title, 
  data, 
  maxHeight = 'h-64' 
}) => {
  // Function to get column search props
  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
      // Use setTimeout to focus input after dropdown is rendered
      setTimeout(() => {
        const input = document.querySelector('.ant-table-filter-dropdown input') as HTMLInputElement;
        if (input) {
          input.focus();
          // Don't select text - just put cursor at the end
          input.setSelectionRange(input.value.length, input.value.length);
        }
      }, 100);
      
      return (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              // Use timeout to debounce the confirm call
              setTimeout(() => confirm({ closeDropdown: false }), 100);
            }}
            onPressEnter={() => confirm()}
            allowClear={{
              clearIcon: '×',
            }}
            style={{ width: 200 }}
          />
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value: any, record: any) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toString().toLowerCase())
        : false,
  });

  const columns = [
    { 
      title: 'Name', 
      dataIndex: 'name', 
      key: 'name', 
      width: '40%',
      ...getColumnSearchProps('name'),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: '25%',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: '%',
      dataIndex: 'percentage',
      key: 'percentage',
      width: '20%',
      render: (percentage: number) => formatPercentage(percentage),
    },
    {
      title: 'Action',
      key: 'action',
      width: '15%',
      render: (_: any, record: TableItem) => <Link to={record.link}>View</Link>,
    },
  ];

  const tableData = data.map((item, index) => ({
    key: index,
    ...item,
  }));

  return (
    <div>
      <h4 className="mb-4">{title}</h4>
      <div className={`${maxHeight} overflow-hidden border border-gray-200 rounded`}>
        <Table
          dataSource={tableData}
          columns={columns}
          pagination={false}
          scroll={{ y: 240 }}
          size="small"
        />
      </div>
    </div>
  );
};

export default DataTable;
